import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, ShieldCheck, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-20">
        <div className="max-w-md w-full text-center">
          <div className="glass p-8 rounded-sm">
            <h1 className="text-2xl font-heading font-bold mb-4 text-red-400">Invalid Reset Link</h1>
            <p className="text-textSecondary mb-6">No reset token found. Please request a new password reset.</p>
            <Link to="/forgot-password" className="bg-primary hover:bg-primaryHover text-white rounded-sm font-bold px-6 py-3 inline-block transition-colors">
              Request New Reset
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-20">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-heading font-bold mb-2" data-testid="reset-password-title">
            Set New Password
          </h1>
          <p className="text-textSecondary">Choose a strong password for your account</p>
        </div>

        <div className="glass p-8 rounded-sm">
          {success ? (
            <div className="text-center space-y-4" data-testid="reset-success">
              <div className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded-sm">
                Password reset successful! You can now log in with your new password.
              </div>
              <button
                onClick={() => navigate('/login')}
                data-testid="go-to-login-button"
                className="w-full bg-primary hover:bg-primaryHover text-white rounded-sm font-bold tracking-wide uppercase py-3 transition-all duration-300 red-glow"
              >
                Go to Login
              </button>
            </div>
          ) : (
            <>
              {error && (
                <div className="bg-red-500/20 border border-red-500 text-red-500 px-4 py-3 rounded-sm mb-6" data-testid="reset-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">New Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-textSecondary" />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      data-testid="new-password-input"
                      className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm pl-12 pr-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary placeholder:text-white/30"
                      placeholder="Min 6 characters"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Confirm Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-textSecondary" />
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      data-testid="confirm-password-input"
                      className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm pl-12 pr-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary placeholder:text-white/30"
                      placeholder="Repeat password"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="reset-submit-button"
                  className="w-full bg-primary hover:bg-primaryHover text-white rounded-sm font-bold tracking-wide uppercase py-3 transition-all duration-300 red-glow disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            </>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-primary hover:underline font-medium flex items-center justify-center space-x-2">
              <ArrowLeft className="w-4 h-4" /><span>Back to Login</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
